<template lang="pug">
.wrapper
  section.hero
    .hero__description
      .hero__info
        h2.hero__title Клиент
        p.hero__text Любительская футбольная Лига России
      .hero__info
        h2.hero__title Задача
        p.hero__text Спроектировать сайт, позволяющий всем фанатам и участникам ЛФЛ быть в курсе событий
    img.hero__image(
      src="/portfolio/lfl/lfl-hero.svg"
      alt='')
  section.section.palette
    PortfolioSectionTitle(title='Палитра')
    ul.palette__items
      li.palette__item(v-for='item in palette')
        .palette__color(:style='{backgroundColor: item}')
        span.palette__description {{ item }}
  section.section.typeface
    PortfolioSectionTitle(title='Типографика' subtitle='Гарнитуры')
    PortfolioTypefaceComponent(:typeface='typeface')
  section.section.typo
    PortfolioSectionTitle(title='Типографика' subtitle='Использование')
    .typo__headers
      .typo__item
        .typo__header.typo__header--h1 H1
        .typo__text.typo__text--t1 Рекламные заголовки
      .typo__item
        .typo__header.typo__header--h1 H2
        .typo__text.typo__text--t2 Подзаголовки новостей
    .typo__body
      .typo__item.typo__item--body
        .typo__header.typo__header--h2 body1
        .typo__text.typo__text--t3 Текст
      .typo__item.typo__item--body
        .typo__header.typo__header--h2 body2
        .typo__text.typo__text--t4 Текст таблицы
      .typo__item.typo__item--body
        .typo__header.typo__header--h2 tags
        .typo__text.typo__text--t5 Маленькие теги
  section.section.main
    PortfolioSectionTitle(title='До/После' subtitle='Главная')
    PortfolioWindowComponent(:beforeAfterImageUrls='beforeAfterImageUrls.main' prefix='main')
  section.section.news
    PortfolioSectionTitle(title='До/После' subtitle='Новости')
    PortfolioWindowComponent(:beforeAfterImageUrls='beforeAfterImageUrls.news' prefix='news')
  section.section.shedule
    PortfolioSectionTitle(title='До/После' subtitle='Расписание')
    PortfolioWindowComponent(:beforeAfterImageUrls='beforeAfterImageUrls.shedule' prefix='shedule')
  section.section.main-page
    PortfolioSectionTitle(title='Главная страница')
    picture.section__image
      source(
        type='image/avif'
        srcset='/portfolio/lfl/main-min.avif'
        media='(max-width: 767px)')
      source(
        type='image/avif'
        srcset='/portfolio/lfl/main.avif')
      source(
        type='image/webp'
        srcset='/portfolio/lfl/main-min.webp'
        media='(max-width: 767px)')
      source(
        type='image/webp'
        srcset='/portfolio/lfl/main.webp')
      source(
        srcset='/portfolio/lfl/main-min.jpg'
        media='(max-width: 767px)')
      img(
        src='/portfolio/lfl/main.jpg'
        loading='lazy'
        alt='')
  section.section.opportunities
    PortfolioSectionTitle(title='Возможности')
    PortfolioArticleComponent(v-for='article in articlesData' :article='article')
  section.section.mobile
    PortfolioSectionTitle(title='Мобильная версия')
    .mobile__wrapper
      template(v-for='(item, idx) in mobileData' :key='idx')
        picture.section__image.mobile__image
          source(
            type='image/avif'
            :srcset='item.pictureData[0].imgSrcMin'
            media='(max-width: 767px)')
          source(
            type='image/avif'
            :srcset='item.pictureData[0].imgSrc')
          source(
            type='image/webp'
            :srcset='item.pictureData[1].imgSrcMin'
            media='(max-width: 767px)')
          source(
            type='image/webp'
            :srcset='item.pictureData[1].imgSrc')
          source(
            :srcset='item.pictureData[2].imgSrcMin'
            media='(max-width: 767px)')
          img(
            :src='item.pictureData[2].imgSrc'
            loading='lazy'
            :alt='item.pictureData[2].alt')
  BaseFooter(:formValue="footerData")
</template>

<script setup>
import { projectsContent, lflArticlesData, lflMobileData, lflBeforeAfterImageUrls, lflPalette, lflTypeface } from '~/data/portfolio/projects'
import { useMediaQuery } from '@vueuse/core'
import { extraSmall } from '~/helpers/breakpoints'

definePageMeta({
  layout: 'fullscreen',
})

const id = ref('lfl')
const footerData = reactive({ form: 2 })
const isExtraSmall = reactive({})
const palette = reactive([...lflPalette])
const typeface = reactive([...lflTypeface])
const articlesData = reactive([...lflArticlesData])
const mobileData = reactive([...lflMobileData])
const beforeAfterImageUrls = reactive({ ...lflBeforeAfterImageUrls })

const currentProject = computed(() => projectsContent.find((project) => project.id === id.value)).value.value

usePageHeadMeta(currentProject?.meta?.title, currentProject?.meta?.search_description, currentProject?.meta.og)

onMounted(() => {
  isExtraSmall.value = useMediaQuery(extraSmall)
})
</script>

<style lang='scss' scoped>
p {
  margin: 0;
  color: var(--color-paragraph);
}

img {
  max-width: 100%;
}

.wrapper {
  --gap: 20px;
  --color-main: var(--lfl-main-color);
  --color-dark: var(--lfl-dark-color);
  --color-paragraph: var(--light3-grey-color);
  --font-family: var(--font-rubik);

  display: grid;
  grid-template-columns:
    [full-start] 1fr [wrapper-start] minmax(0, 1172px) [wrapper-end] 1fr [full-end];
  column-gap: var(--gap, 20px);
  color: var(--color-paragraph);
  line-height: 1.4;

  padding-top: 160px;

  @media (--medium-viewport-df) {
    padding-top: 20px;
  }

  @media (--small-viewport-df) {
    padding-top: 17px;
  }
}

.wrapper>* {
  grid-column: wrapper;
}

.hero {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  column-gap: var(--gap, 20px);
}

.hero {
  padding-bottom: 54px;

  @media (--small-viewport-df) {
    padding-bottom: 100px;
  }

  &__description {
    --color-paragraph: var(--lfl-grey-color);

    grid-column: 1 / 3;
    margin-top: 70px;

    @media (--small-viewport-df) {
      grid-column: 1 / -1;
      grid-row-start: 2;
      margin-top: 24px;
    }
  }

  &__info+.hero__info {
    margin-top: 50px;
  }

  &__image {
    grid-column: 3 / -1;
    margin-left: calc((100% - 180px) / 10);
    margin-top: -76px;

    @media (width <=1340px) {
      margin-left: 0;
    }

    @media (--small-viewport-df) {
      grid-column-start: 1;
      margin-left: 0;
      margin-top: 0;
    }
  }

  &__title {
    text-transform: uppercase;
    color: var(--color-main);
    font-size: 34px;
    font-weight: 700;
    margin-bottom: 19px;

    @media (--small-viewport-df) {
      font-size: 20px;
      margin-bottom: 16px;
    }
  }
}

.section {
  --color-header: var(--color-dark);
  --color-subtitle: var(--color-main);

  margin-bottom: 100px;
  margin-bottom: clamp(6.25rem, 1.009rem + 15.53vw, 10.938rem);
}

.palette {
  &__items {
    display: flex;
  }

  &__item {
    flex-basis: calc(100% / 3);
  }

  &__color {
    width: 100%;
    height: 100%;
    margin-bottom: 16px;
    height: 123px;

    @media (--mobile-viewport-df) {
      height: 150px;
    }
  }

  &__description {
    font-size: clamp(1.25rem, 0.272rem + 2.9vw, 2.125rem);
    color: var(--color-dark);
    display: block;
    text-align: center;
  }
}

.typeface {
  --title-color: var(--dark2-color);
  --text-color: var(--light3-grey-color);
}

.typo {
  &__headers,
  &__body,
  &__item {
    display: flex;
  }

  &__headers,
  &__body {
    justify-content: space-between;

    @media (width <=370px) {
      flex-wrap: wrap;
    }
  }

  &__headers {
    margin-bottom: clamp(2.25rem, -0.405rem + 7.87vw, 4.625rem);
    gap: 20px;
  }

  &__item {
    align-items: baseline;

    @media (--small-viewport-df) {
      flex-direction: column;

      &--body {
        justify-content: space-between;
      }
    }
  }

  &__header,
  &__text {
    font-family: var(--font-roboto);
  }

  &__header {
    color: var(--lfl-blue-color);

    &--h1 {
      text-transform: uppercase;
      font-size: clamp(2.25rem, 0.293rem + 5.8vw, 4rem);
      margin-right: 20px;
    }

    &--h2 {
      font-size: clamp(1rem, -0.398rem + 4.14vw, 2.25rem);
      margin-right: 18px;
    }

  }

  &__text {
    color: var(--lfl-dark-color);

    &--t1 {
      font-size: clamp(1.75rem, 0.352rem + 4.14vw, 3rem);
      font-weight: 700;
    }

    &--t2 {
      font-family: var(--font-ubuntu);
      font-size: clamp(1.25rem, 0.132rem + 3.31vw, 2.25rem);
      font-weight: 700;
    }

    &--t3 {
      font-size: clamp(1.25rem, 0.132rem + 3.31vw, 2.25rem);
    }

    &--t4 {
      font-size: clamp(0.625rem, -0.353rem + 2.9vw, 1.5rem);
      font-weight: 400;
    }

    &--t5 {
      font-size: clamp(0.625rem, -0.353rem + 2.9vw, 1.5rem);
      font-weight: 300;
    }
  }
}

.mobile {
  &__wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 66px;
    justify-content: space-between;
    padding-top: 75px;

    @media (--mobile-viewport-df) {
      gap: 20px;
      padding-top: 0;
    }
  }

  &__image {
    width: 282px;

    @media (--mobile-viewport-df) {
      width: 158px;
    }
  }
}
</style>
